// src/components/layout/Sidebar.jsx
import React, { useContext, useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Box,
  Typography,
  Divider,
  useTheme,
  alpha
} from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { UserContext } from '../../context/UserContext';
import { navigationItems, hasAccess, CATEGORIES } from '../../config/navigationConfig';
import { APP_BRANDING } from '../../config/app-branding';

const Sidebar = ({ open, onClose }) => {
  const theme = useTheme();
  const { userRole, userPermissions } = useContext(UserContext);
  const location = useLocation();
  const [openSections, setOpenSections] = useState({});

  const isSubItemActive = useCallback((basePath) => {
    return basePath && location.pathname.startsWith(basePath);
  }, [location.pathname]);

  useEffect(() => {
    let activeSectionTitle = null;
    for (const item of navigationItems) {
      if (item.subItems && isSubItemActive(item.basePath)) {
        activeSectionTitle = item.title;
        break;
      }
    }

    if (activeSectionTitle && !openSections[activeSectionTitle]) {
      setOpenSections(prev => ({ ...prev, [activeSectionTitle]: true }));
    }
  }, [location.pathname, isSubItemActive, openSections]);

  const handleSectionClick = (title) => {
    setOpenSections(prev => ({ ...prev, [title]: !prev[title] }));
  };

  const renderNavGroup = (items) => {
    return items.map((item) => {
      // Filtrar subitems por permisos
      const accessibleSubItems = item.subItems?.filter(sub => hasAccess(sub, userRole, userPermissions)) || [];
      const hasVisibleSubItems = accessibleSubItems.length > 0;

      const buttonStyle = (isActive) => ({
          borderRadius: 2.5,
          mb: 0.5,
          backgroundColor: isActive ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
          color: isActive ? 'primary.main' : 'text.primary',
          '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.04),
              color: 'primary.main',
          },
          '& .MuiListItemIcon-root': {
              color: isActive ? 'primary.main' : 'text.secondary',
          }
      });

      if (hasVisibleSubItems) {
        const isOpen = openSections[item.title] || isSubItemActive(item.basePath);
        const isActiveParent = isSubItemActive(item.basePath);
        
        return (
          <React.Fragment key={item.title}>
            <ListItemButton 
              onClick={() => handleSectionClick(item.title)} 
              sx={buttonStyle(isActiveParent)}
            >
              <ListItemIcon sx={{ minWidth: 38 }}>
                {React.cloneElement(item.icon, { fontSize: 'small' })}
              </ListItemIcon>
              <ListItemText 
                primary={item.title} 
                primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: isActiveParent ? 700 : 500 }} 
              />
              {isOpen ? <ExpandLess fontSize="small" opacity={0.5} /> : <ExpandMore fontSize="small" opacity={0.5} />}
            </ListItemButton>
            
            <Collapse in={isOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding sx={{ mb: 1 }}>
                {accessibleSubItems.map((subItem) => (
                  <ListItemButton
                    key={subItem.title}
                    component={Link}
                    to={subItem.path}
                    onClick={onClose}
                    selected={location.pathname === subItem.path}
                    sx={{
                        pl: 6,
                        borderRadius: 2,
                        mb: 0.2,
                        py: 0.8,
                        color: location.pathname === subItem.path ? 'primary.main' : 'text.secondary',
                        '&.Mui-selected': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.05),
                            color: 'primary.main',
                            '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.1) },
                            '&:before': {
                                content: '""',
                                position: 'absolute',
                                left: 28,
                                width: 5,
                                height: 5,
                                borderRadius: '50%',
                                backgroundColor: 'primary.main'
                            }
                        }
                     }}
                  >
                    <ListItemText primary={subItem.title} primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: location.pathname === subItem.path ? 600 : 400 }} />
                  </ListItemButton>
                ))}
              </List>
            </Collapse>
          </React.Fragment>
        );
      }
      else if (!item.subItems && item.path) {
        const isActive = location.pathname === item.path;
        return (
          <ListItemButton
            key={item.title}
            component={Link}
            to={item.path}
            onClick={onClose}
            selected={isActive}
            sx={buttonStyle(isActive)}
          >
            <ListItemIcon sx={{ minWidth: 38 }}>
               {React.cloneElement(item.icon, { fontSize: 'small' })}
            </ListItemIcon>
            <ListItemText primary={item.title} primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: isActive ? 700 : 500 }} />
          </ListItemButton>
        );
      }
      return null;
    });
  };

  const hrItems = navigationItems.filter(item => item.category === CATEGORIES.HR && hasAccess(item, userRole, userPermissions));
  const systemItems = navigationItems.filter(item => item.category === CATEGORIES.SYSTEM && hasAccess(item, userRole, userPermissions));

  return (
    <Drawer 
      variant="temporary" 
      open={open} 
      onClose={onClose} 
      anchor="left"
      sx={{
          '& .MuiDrawer-paper': { 
              width: 280, 
              boxSizing: 'border-box',
              borderRight: 'none',
              boxShadow: theme.shadows[10]
          },
      }}
    >
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 1 }}>
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 900, 
            letterSpacing: '2px', 
            color: theme.palette.primary.main,
            fontSize: '1.2rem' 
          }}
        >
            {APP_BRANDING.name}
        </Typography>
        <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600, opacity: 0.6 }}>
            {APP_BRANDING.tagline}
        </Typography>
      </Box>
      <Divider sx={{ mx: 2, mb: 2, opacity: 0.5 }} />

      <List component="nav" sx={{ px: 2, pb: 4 }}>
        <Typography variant="caption" sx={{ px: 2, mb: 1, display: 'block', fontWeight: 700, opacity: 0.4, textTransform: 'uppercase', letterSpacing: '1px' }}>
          Recursos Humanos
        </Typography>
        {renderNavGroup(hrItems)}

        {systemItems.length > 0 && (
          <>
            <Divider sx={{ my: 3, mx: 2, opacity: 0.3 }} />
            <Typography variant="caption" sx={{ px: 2, mb: 1, display: 'block', fontWeight: 700, opacity: 0.4, textTransform: 'uppercase', letterSpacing: '1px' }}>
              Configuración
            </Typography>
            {renderNavGroup(systemItems)}
          </>
        )}
      </List>
    </Drawer>
  );
};

export default Sidebar;
